import React from 'react';
import { Title2, Button, makeStyles, tokens, Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions, Input, Select, Textarea, Spinner } from '@fluentui/react-components';
import { ChevronLeftRegular, ChevronRightRegular, CalendarRegular, PeopleRegular } from '@fluentui/react-icons';
import { getSharePointService } from '../services/sharePointService';

// Canonical departments matching Org Chart (source of truth)
const CANONICAL_DEPARTMENTS = [
  'Marketing and Communications',
  'Clinical Operations',
  'Finance & Development',
  'LEHR',
  'Support Services',
  'Technology'
];

type CalendarEvent = {
  id: string;
  title: string;
  date: string; // yyyy-mm-dd
  startTime?: string; // HH:mm
  endTime?: string; // HH:mm
  department?: string;
  location?: string;
  description?: string;
  attendeesCsv?: string; // comma-separated names (demo only)
};

const useStyles = makeStyles({
  container: { display: 'flex', flexDirection: 'column', gap: '16px' },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  monthNav: { display: 'flex', alignItems: 'center', gap: '8px' },
  metaRow: { display: 'flex', alignItems: 'center', gap: '8px' },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: '8px',
  },
  weekday: {
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
    paddingBottom: '4px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    fontWeight: tokens.fontWeightSemibold,
  },
  dayCell: {
    minHeight: '110px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '8px',
    padding: '8px',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  dayHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  dayNumber: { fontWeight: tokens.fontWeightSemibold },
  otherMonth: { color: tokens.colorNeutralForeground3 },
  eventPill: {
    fontSize: '12px',
    padding: '4px 6px',
    borderRadius: '6px',
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    cursor: 'pointer',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  filters: { display: 'flex', alignItems: 'center', gap: '8px' },
});

const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
const toKey = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const startOfMonthGrid = (year: number, month: number) => {
  const first = new Date(year, month, 1);
  const start = new Date(first);
  start.setDate(first.getDate() - ((first.getDay() + 6) % 7)); // Monday-first grid
  const days: Date[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    days.push(d);
  }
  return days;
};

const CalendarPage: React.FC = () => {
  const styles = useStyles();
  const [cursor, setCursor] = React.useState(() => new Date());
  const [departmentFilter, setDepartmentFilter] = React.useState<string>('all');
  const [events, setEvents] = React.useState<CalendarEvent[]>([]);
  const [editorOpen, setEditorOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<CalendarEvent | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const monthDays = startOfMonthGrid(year, month);

  // Load events from SharePoint on mount
  React.useEffect(() => {
    const loadEvents = async () => {
      try {
        setLoading(true);
        const sharePointService = getSharePointService();
        const sharePointEvents = await sharePointService.getCalendarEvents();
        
        // Convert SharePoint events to CalendarEvent format
        const calendarEvents: CalendarEvent[] = sharePointEvents.map((e: any) => ({
          id: e.id,
          title: e.title,
          date: e.date,
          startTime: e.startTime,
          endTime: e.endTime,
          department: e.department,
          location: e.location,
          description: e.description,
          attendeesCsv: e.attendeesCsv,
        }));
        
        setEvents(calendarEvents);
      } catch (error) {
        console.error('Error loading calendar events:', error);
        // Continue with empty events if there's an error
      } finally {
        setLoading(false);
      }
    };
    
    loadEvents();
  }, []);

  const openNewForDate = (d: Date) => {
    setEditing({
      id: crypto.randomUUID(),
      title: '',
      date: toKey(d),
      startTime: '09:00',
      endTime: '10:00',
      department: 'Operations',
      location: '',
      description: '',
      attendeesCsv: '',
    });
    setEditorOpen(true);
  };

  const openEdit = (evt: CalendarEvent) => {
    setEditing({ ...evt });
    setEditorOpen(true);
  };

  const saveEvent = async () => {
    if (!editing) return;
    
    try {
      setSaving(true);
      const sharePointService = getSharePointService();
      
      // Check if this is a new event (UUID format means it's new, numeric ID means it's from SharePoint)
      const isNewEvent = editing.id.includes('-') && editing.id.length === 36; // UUID format
      const existingEvent = events.find(e => e.id === editing.id && !e.id.includes('-'));
      
      if (existingEvent || (!isNewEvent && !editing.id.includes('-'))) {
        // Update existing event in SharePoint
        await sharePointService.updateCalendarEvent(editing.id, {
          title: editing.title,
          date: editing.date,
          startTime: editing.startTime,
          endTime: editing.endTime,
          department: editing.department,
          location: editing.location,
          description: editing.description,
          attendeesCsv: editing.attendeesCsv,
        });
      } else {
        // Create new event in SharePoint
        const newId = await sharePointService.createCalendarEvent({
          title: editing.title,
          date: editing.date,
          startTime: editing.startTime,
          endTime: editing.endTime,
          department: editing.department,
          location: editing.location,
          description: editing.description,
          attendeesCsv: editing.attendeesCsv,
        });
        
        // Update the editing event with the SharePoint ID
        editing.id = newId;
      }
      
      // Update local state
      setEvents(prev => {
        const idx = prev.findIndex(e => e.id === editing.id);
        if (idx >= 0) {
          const copy = [...prev];
          copy[idx] = editing;
          return copy;
        }
        return [...prev, editing];
      });
      
      setEditorOpen(false);
    } catch (error) {
      console.error('Error saving calendar event:', error);
      alert('Failed to save event. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const deleteEvent = async (id?: string) => {
    if (!id) return;
    
    try {
      const sharePointService = getSharePointService();
      
      // Only try to delete from SharePoint if it's a SharePoint ID (not a UUID)
      // SharePoint IDs are numeric strings, UUIDs contain hyphens
      if (id && !id.includes('-')) {
        await sharePointService.deleteCalendarEvent(id);
      }
      
      // Update local state
      setEvents(prev => prev.filter(e => e.id !== id));
      setEditorOpen(false);
    } catch (error) {
      console.error('Error deleting calendar event:', error);
      alert('Failed to delete event. Please try again.');
    }
  };

  const exportIcs = (evt: CalendarEvent) => {
    const dt = evt.date.replace(/-/g, '');
    const start = `${dt}T${(evt.startTime || '0900').replace(':', '')}00Z`;
    const end = `${dt}T${(evt.endTime || '1000').replace(':', '')}00Z`;
    const body = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//TruCare//Calendar//EN',
      'BEGIN:VEVENT',
      `UID:${evt.id}@trucare`,
      `DTSTAMP:${start}`,
      `DTSTART:${start}`,
      `DTEND:${end}`,
      `SUMMARY:${evt.title}`,
      evt.location ? `LOCATION:${evt.location}` : '',
      evt.description ? `DESCRIPTION:${evt.description}` : '',
      'END:VEVENT',
      'END:VCALENDAR',
    ].filter(Boolean).join('\r\n');
    const blob = new Blob([body], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${evt.title || 'event'}.ics`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filteredEvents = events.filter(e => departmentFilter === 'all' || e.department === departmentFilter);
  const dateToEvents: Record<string, CalendarEvent[]> = {};
  filteredEvents.forEach(e => {
    (dateToEvents[e.date] ||= []).push(e);
  });

  const monthName = cursor.toLocaleString('en-US', { month: 'long', year: 'numeric' });

  if (loading) {
    return (
      <div className={styles.container} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Spinner label="Loading calendar events..." />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.headerRow}>
        <div className={styles.monthNav}>
          <Button appearance="subtle" icon={<ChevronLeftRegular />} onClick={() => setCursor(new Date(year, month - 1, 1))} />
          <Title2>{monthName}</Title2>
          <Button appearance="subtle" icon={<ChevronRightRegular />} onClick={() => setCursor(new Date(year, month + 1, 1))} />
        </div>
        <div className={styles.filters}>
          <Select value={departmentFilter} onChange={(e) => setDepartmentFilter(e.target.value)}>
            <option value="all">All Departments</option>
            {CANONICAL_DEPARTMENTS.map(unit => (
              <option key={unit} value={unit}>{unit}</option>
            ))}
          </Select>
          <Button appearance="primary" icon={<CalendarRegular />} onClick={() => openNewForDate(cursor)}>Add Event</Button>
        </div>
      </div>

      {/* Weekday headers */}
      <div className={styles.grid}>
        {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(d => (
          <div key={d} className={styles.weekday}>{d}</div>
        ))}
        {monthDays.map((d) => {
          const key = toKey(d);
          const isOther = d.getMonth() !== month;
          const dayEvents = dateToEvents[key] || [];
          return (
            <div key={key} className={styles.dayCell} onDoubleClick={() => openNewForDate(d)}>
              <div className={styles.dayHeader}>
                <span className={`${styles.dayNumber} ${isOther ? styles.otherMonth : ''}`}>{d.getDate()}</span>
                <Button size="small" appearance="subtle" onClick={() => openNewForDate(d)}>+</Button>
              </div>
              {dayEvents.map(evt => (
                <div key={evt.id} className={styles.eventPill} onClick={() => openEdit(evt)} title={`${evt.title}${evt.department ? ' • ' + evt.department : ''}`}>
                  {evt.startTime ? `${evt.startTime} ` : ''}{evt.title}
                </div>
              ))}
              {dayEvents.length === 0 && (
                <div style={{ color: tokens.colorNeutralForeground4, fontSize: '12px' }}>No events</div>
              )}
            </div>
          );
        })}
      </div>

      {editorOpen && editing && (
        <Dialog open onOpenChange={() => setEditorOpen(false)}>
          <DialogSurface>
            <DialogBody>
              <DialogTitle>{editing.title ? 'Edit Event' : 'Add Event'}</DialogTitle>
              <DialogContent>
                <div style={{ display: 'grid', gap: '12px', gridTemplateColumns: '1fr 1fr' }}>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} placeholder="Event title" />
                  </div>
                  <div>
                    <Input type="date" value={editing.date} onChange={(e) => setEditing({ ...editing, date: e.target.value })} />
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <Input type="time" value={editing.startTime} onChange={(e) => setEditing({ ...editing, startTime: e.target.value })} />
                    <Input type="time" value={editing.endTime} onChange={(e) => setEditing({ ...editing, endTime: e.target.value })} />
                  </div>
                  <div>
                    <Select value={editing.department || ''} onChange={(e) => setEditing({ ...editing, department: e.target.value })}>
                      <option value="">Department (optional)</option>
                      {CANONICAL_DEPARTMENTS.map(unit => (
                        <option key={unit} value={unit}>{unit}</option>
                      ))}
                    </Select>
                  </div>
                  <div>
                    <Input placeholder="Location (optional)" value={editing.location || ''} onChange={(e) => setEditing({ ...editing, location: e.target.value })} />
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <Textarea placeholder="Description (optional)" value={editing.description || ''} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
                  </div>
                  <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <PeopleRegular />
                    <Input placeholder="Attendees (comma-separated names for demo)" value={editing.attendeesCsv || ''} onChange={(e) => setEditing({ ...editing, attendeesCsv: e.target.value })} />
                  </div>
                </div>
              </DialogContent>
              <DialogActions>
                {editing.id && (
                  <Button appearance="secondary" onClick={() => exportIcs(editing)}>Export .ics</Button>
                )}
                {editing.id && (
                  <Button appearance="subtle" onClick={() => deleteEvent(editing.id)}>Delete</Button>
                )}
                <Button appearance="primary" onClick={saveEvent} disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </Button>
                <Button onClick={() => setEditorOpen(false)}>Close</Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      )}
    </div>
  );
};

export default CalendarPage;


